import React, { useRef, useMemo, useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import HelpOutlineIcon from '@mui/icons-material/Help';
import AiIcon from '@mui/icons-material/AutoFixHigh';
import CloseIcon from '@mui/icons-material/Close';
import AdvanceTextEditor, {
  htmlToText,
} from 'components/form/AdvanceTextEditor';
import AttachmentToolbar from './AttachmentToolbar';
import VariablesToolbar from './VariablesToolbar';
import SmsCounter from 'lib/SmsCounter';
import Dropdown from 'components/Dropdown';
import IconButton from 'components/IconButton';
import Button from 'components/Button';
import { AIButtonIcon } from 'components/icons';
import toast, { snack } from 'utils/toast';
import { Trans, useTranslation } from 'react-i18next';
import {
  shortenMessage,
  enhanceMessage,
  getAIReply,
} from 'services/messages.svc';
import useAlert from 'hooks/useAlert';
import useVariables from './useVariables';
import EmojiPicker from './EmojiPicker';

const AILoadingText = {
  shorten: 'Shortening your message...',
  enhance: 'Enhancing your message...',
  suggest: 'Suggesting a reply...',
};

const AIResultTitle = {
  shorten: 'Shortened Message',
  enhance: 'Enhanced Message',
  suggest: 'Suggested Reply',
};

const SMSComposer = ({
  id,
  value,
  onChange,
  onFileSelect,
  placeholder,
  multiSegmentWarning,
  messageSizeWarning,
  maxSegments = 3,
  maxFileSize,
  height,
  onBlur,
  disabled,
  onClick,
  hasError,
  helpText,
  aiActions,
}) => {
  const [aiResult, setAiResult] = useState({
    loading: false,
    type: null,
    result: '',
  });
  const { variablesMap, loading, options: variables } = useVariables();
  const alert = useAlert();
  const [t] = useTranslation(['messages']);
  const [alertLevel, setAlertLevel] = useState();
  const [alertMessage, setAlertMessage] = useState();
  const editor = useRef();
  const plainText = htmlToText(value);
  const counter = SmsCounter.count(plainText);
  const count = counter.messages;
  const charCount = counter.length;
  const perMessage = counter.per_message;

  useEffect(() => {
    if (messageSizeWarning) {
      if (count > 1) {
        showMessageAlert();
      } else {
        setAlertLevel();
        setAlertMessage();
      }
    }
  }, [plainText]);

  const showMessageAlert = () => {
    const { per_message, messages, length } = counter;

    if (alertLevel > 2) return;

    let msgKey;

    const maxChars = per_message * 3;

    if (length >= maxChars) {
      msgKey =
        per_message > 150
          ? 'sms-message-count-warning-gsm-max'
          : 'sms-message-count-warning-ucs-max';
      setAlertLevel(2);
    } else if (messages > 1) {
      msgKey =
        per_message > 150
          ? 'sms-message-count-warning-160'
          : 'sms-message-count-warning-70';
      setAlertLevel(1);
    }

    if (!msgKey) return;

    setAlertMessage(
      <Trans
        ns="messages"
        i18nKey={msgKey}
        components={[
          <a
            target="_blank"
            style={{ textDecoration: 'underline' }}
            href="https://help.textinchurch.com/en/articles/4271029-crafting-the-perfect-text-message"
          />,
          <a
            target="_blank"
            style={{ textDecoration: 'underline' }}
            href="https://help.textinchurch.com/en/articles/1807768-carrier-filtering"
          />,
        ]}
      />,
    );
  };

  const onLinkSelect = (link) => {
    editor.current.editor.execCommand('mceInsertContent', false, link.url);
  };

  const onVariableSelect = (variable) => {
    editor.current.editor.execCommand('insertVariable', false, variable);
  };

  const onKeyDown = ({ keyCode }) => {
    if (!maxSegments) return true;
    else if ([8, 16, 20, 37, 38, 39, 40, 46].includes(keyCode)) {
      return true;
    }
    return count <= maxSegments;
  };

  const handleFileSelect = (file) => {
    if (file.file_size === '-1') {
      alert.confirm({
        title: t('unknown-file-size-title'),
        description: t('unknown-file-size-message'),
        onConfirm: () => {
          onFileSelect(file);
        },
      });
    } else if (maxFileSize && file.file_size >= maxFileSize) {
      alert.show({
        title: t('file-too-large-title'),
        description: t('file-too-large-message'),
      });
    } else {
      onFileSelect(file);
    }
  };

  const onQuickReplySelect = (item) => {
    const { template_content, file } = item;
    const text = template_content.replace(/\n/g, '<br />');
    onTextChange(text);
    if (onFileSelect) {
      if (file.hash) {
        handleFileSelect(file);
      } else {
        onFileSelect();
      }
    }
  };

  const onPaste = (content) => {
    return content.substr(0, counter.per_message * maxSegments);
  };

  const onTextChange = (val) => {
    if (val.length < value.length) {
      onChange(val);
    } else {
      const text = htmlToText(val);
      const counter = SmsCounter.count(text);
      if (counter.messages < maxSegments) {
        onChange(val);
      } else {
        if (counter.messages === maxSegments && counter.remaining > 0) {
          onChange(val);
        } else {
          onChange(text.substr(0, counter.per_message * maxSegments));
        }
      }
    }
  };

  const onEmojiSelect = (val) => {
    editor.current.editor.execCommand('mceInsertContent', false, val);
  };

  const onAIActionSelect = (key) => {
    if (!value && key !== 'suggest')
      return snack.info('Please enter a message first.');
    setAiResult({ loading: true, type: key });
    switch (key) {
      case 'shorten': {
        return shortenMessage(value)
          .then(({ content }) => {
            const text = htmlToText(content);
            const counter = SmsCounter.count(text);
            setAiResult({
              loading: false,
              type: key,
              result: text,
              chars: counter.length,
            });
          })
          .catch((err) => {
            setAiResult({});
            toast.error(err.message);
          });
      }
      case 'enhance': {
        return enhanceMessage(value, counter.per_message * 3).then(
          ({ content }) => {
            const text = htmlToText(content);
            const counter = SmsCounter.count(text);
            setAiResult({
              loading: false,
              type: key,
              result: text,
              chars: counter.length,
            });
          },
          (err) => {
            setAiResult({});
            toast.error(err.message);
          },
        );
      }
      case 'suggest': {
        const { contactId } = aiActions.find((a) => a.key === 'suggest');
        return getAIReply(contactId).then((res) => {
          const reply = res.replies[0];
          if (reply) {
            const text = htmlToText(reply);
            const counter = SmsCounter.count(text);
            setAiResult({
              loading: false,
              type: key,
              result: text,
              chars: counter.length,
            });
          } else {
            setAiResult({});
            toast.info('Could not suggest a reply.');
          }
        });
      }
    }
  };

  const onConfirmAiText = () => {
    onTextChange(aiResult.result);
    setAiResult({});
  };

  const onRephrase = () => {
    return onAIActionSelect(aiResult.type);
  };

  return (
    <Paper elevation={0}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="flex-end"
      >
        <Typography color="text.secondary" variant="body2">
          {charCount}/{perMessage} characters - {count} message segments(s)
        </Typography>
        <Tooltip title="Message counts are only an estimate when using Message Variables">
          <HelpOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        </Tooltip>
      </Stack>
      <Box
        position="relative"
        sx={
          alertMessage && {
            'zIndex': 1,
            '& .tox-tinymce, & .tox-tinymce.focused': {
              borderColor: alertLevel === 1 ? 'info.main' : 'error.main',
            },
          }
        }
        my={1}
      >
        <AdvanceTextEditor
          margin="none"
          id={id}
          innerRef={editor}
          value={value}
          onChange={onTextChange}
          onPaste={onPaste}
          onBlur={onBlur}
          onClick={onClick}
          disabled={disabled}
          variables={variablesMap}
          placeholder={placeholder}
          onKeyDown={onKeyDown}
          height={height}
          hasError={hasError}
          helpText={helpText}
        />
        {aiResult.loading ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{
              position: 'absolute',
              bottom: 1,
              left: 1,
              right: 1,
              height: 40,
              bgcolor: 'grey.100',
              borderBottomLeftRadius: 6,
              borderBottomRightRadius: 6,
            }}
          >
            <AiIcon fontSize="small" color="primary" />
            <Typography variant="body2">
              {AILoadingText[aiResult.type]}
            </Typography>
          </Stack>
        ) : (
          aiResult.result && (
            <Box
              sx={{
                py: 1,
                position: 'absolute',
                bottom: 1,
                left: 1,
                right: 1,
                bgcolor: 'grey.100',
                borderRadius: '6px',
              }}
            >
              <Stack
                px={2}
                mb={1}
                direction="row"
                alignItems="center"
                spacing={1}
              >
                <AiIcon fontSize="small" color="primary" />
                <Typography variant="body2" fontWeight={500} flex={1}>
                  {AIResultTitle[aiResult.type]}
                </Typography>
                <span>
                  <IconButton
                    edge="end"
                    icon={<CloseIcon fontSize="small" />}
                    size="small"
                    onClick={() => setAiResult({})}
                  />
                </span>
              </Stack>
              <Stack px={2}>
                <Typography paragraph sx={{ maxHeight: 80, overflow: 'auto' }}>
                  {aiResult.result}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {aiResult.chars} characters
                </Typography>
              </Stack>
              <Stack
                mt={1}
                px={1}
                direction="row"
                justifyContent="space-between"
              >
                <Button size="small" variant="text" onClick={onRephrase}>
                  Rephrase
                </Button>
                <Button size="small" variant="text" onClick={onConfirmAiText}>
                  Use this Message
                </Button>
              </Stack>
            </Box>
          )
        )}
        {!aiResult.type && (
          <Dropdown
            onSelect={onAIActionSelect}
            options={[
              ...(aiActions || []),
              { key: 'shorten', children: 'Shorten Message' },
              { key: 'enhance', children: 'Enhance Message' },
            ]}
          >
            <AIButtonIcon
              sx={{
                width: 32,
                height: 32,
                position: 'absolute',
                bottom: 10,
                right: 10,
                cursor: 'pointer',
              }}
            />
          </Dropdown>
        )}
      </Box>
      {alertMessage && (
        <Box sx={{ position: 'relative', marginTop: '-12px' }}>
          <Alert
            sx={{
              'fontWeight': 500,
              'position': 'relative',
              'fontSize': 14,
              'borderTopLeftRadius': 0,
              'borderTopRightRadius': 0,
              'borderTop': 0,
              'alignItems': 'center',
              'bgcolor': alertLevel === 1 ? 'info.lighter' : 'error.lighter',
              'color': alertLevel === 1 ? 'info.main' : 'error.main',
              'borderColor': alertLevel === 1 ? 'info.main' : 'error.main',
              '& a': {
                color: 'inherit',
              },
            }}
            icon={false}
            variant="outlined"
            action={
              <Button
                onClick={() => onAIActionSelect('shorten')}
                startIcon={<AiIcon />}
              >
                Shorten
              </Button>
            }
          >
            {alertMessage}
          </Alert>
        </Box>
      )}
      <Stack direction="row" mt={1} spacing={1}>
        <EmojiPicker onSelect={onEmojiSelect} />
        <VariablesToolbar
          loading={loading}
          variables={variables}
          onSelect={onVariableSelect}
        />
        <AttachmentToolbar
          type="sms"
          onLinkSelect={onLinkSelect}
          onFileSelect={onFileSelect && handleFileSelect}
          onQuickReplySelect={onQuickReplySelect}
        />
      </Stack>
      {/*{multiSegmentWarning && count > 1 && (*/}
      {/*  <Typography color="warning.main" align="center" variant="body2">*/}
      {/*    {multiSegmentWarningText}*/}
      {/*  </Typography>*/}
      {/*)}*/}
    </Paper>
  );
};

export default React.forwardRef((props, ref) => (
  <SMSComposer {...props} forwardedRef={ref} />
));
